import styled from '@emotion/styled';
import { IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useMixpanel from 'hooks/useMixpanel';
import React, { InputHTMLAttributes, useCallback, useRef } from 'react';
import { titleCase } from 'utils/helpers';

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: theme.baseUnit,
  width: '100%',
}));

interface InputContainerProps extends React.BaseHTMLAttributes<HTMLDivElement> {
  isInvalid: boolean;
  isDisabled: boolean;
}

const InputContainer: React.FC<InputContainerProps> = styled('div')(
  ({ isInvalid, isDisabled, theme }) => ({
    cursor: isDisabled ? 'default' : 'text',
    backgroundColor: isDisabled
      ? theme.colors.alias.primaryDisabled
      : theme.colors.alias.mainSurface,
    padding: `${theme.baseUnit}px ${theme.baseUnit * 2}px`,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.baseUnit,
    borderRadius: theme.baseUnit,
    border: '1px solid',
    borderColor:
      isInvalid && !isDisabled ? theme.colors.alias.statusError : 'transparent',
    '&:focus-within': {
      borderColor: isDisabled
        ? 'transparent'
        : isInvalid
          ? theme.colors.alias.statusError
          : theme.colors.alias.primary,
    },
    '&:hover': {
      borderColor: isDisabled
        ? 'transparent'
        : isInvalid
          ? theme.colors.alias.statusError
          : theme.colors.alias.primary,
    },
  }),
);

const labelTransform = 'translate(0px, -20px) scale(0.7)';
const TextContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: theme.baseUnit,
  width: '100%',
  '&:focus-within label': {
    transform: labelTransform,
  },
  position: 'relative',
}));

const Input = styled('input')(({ theme }) => ({
  zIndex: 10,
  ...theme.typography.fontFamily.reader400,
  color: theme.colors.alias.textPrimary,
  backgroundColor: 'transparent',
  fontSize: '16px',
  border: 'none',
  padding: '20px 0 5px',
  outline: 'none',
}));

interface LabelProps extends React.BaseHTMLAttributes<HTMLLabelElement> {
  filled?: boolean;
}

const Label: React.FC<LabelProps> = styled('label')(({ filled, theme }) => ({
  cursor: 'text',
  ...theme.typography.fontFamily.reader400,
  fontSize: '18px',
  lineHeight: '18px',
  letterSpacing: '.5px',
  color: theme.colors.alias.textSecondary,
  position: 'absolute',
  top: '50%',
  transformOrigin: 'top left',
  transform: filled ? labelTransform : 'translate(0px, -50%)',
  transition: '200ms cubic-bezier(0, 0, 0.2, 1) 0ms',
  zIndex: 11, // Ensure label is above input
}));

interface HelperTextProps extends React.BaseHTMLAttributes<HTMLSpanElement> {
  isInvalid: boolean;
  isDisabled: boolean;
}

const HelperText: React.FC<HelperTextProps> = styled('span')(
  ({ isInvalid, isDisabled, theme }) => ({
    ...theme.typography.fontFamily.reader400,
    fontSize: '12px',
    lineHeight: '18px',
    color: isInvalid
      ? theme.colors.alias.statusError
      : isDisabled
        ? theme.colors.alias.textDisabled
        : theme.colors.alias.textSecondary,
  }),
);

export type NewTextFieldProps = {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label?: string;
  helperText?: string;
  iconLeft?: IconDefinition;
  iconRight?: IconDefinition;
  onIconRightClick?: () => void;
  inputProps?: InputHTMLAttributes<HTMLElement>;
  isInvalid?: boolean;
  isDisabled?: boolean;
  mixpanelData?: Record<string, string>;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  style?: React.CSSProperties;
};

/**
 * A new standard text input with a mixpanel event baked in.
 */
const NewTextField = (props: NewTextFieldProps) => {
  const {
    value,
    onChange,
    label,
    helperText,
    iconLeft,
    iconRight,
    onIconRightClick,
    isInvalid = false,
    isDisabled = false,
    onBlur,
    mixpanelData,
    inputProps,
    ...rest
  } = props;

  const track = useMixpanel();

  const inputRef = useRef<HTMLInputElement>(null);

  const trackedOnBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      track('User Action', {
        event_name: 'text_entered',
        event_type: 'screen interaction',
        event_label: titleCase(label),
        // if you need to override event_label (ex. to preserve case)
        // just pass the preferred event_label in mixpanelData
        ...mixpanelData,
      });

      if (onBlur) {
        onBlur(e);
      }
    },
    [label, mixpanelData, onBlur, track],
  );

  return (
    <Container {...rest}>
      <InputContainer
        onClick={() => inputRef.current?.focus()}
        isInvalid={isInvalid}
        isDisabled={isDisabled}
      >
        {iconLeft && <FontAwesomeIcon icon={iconLeft} style={{ height: 20 }} />}
        <TextContainer>
          <Label filled={!!value}>{label}</Label>
          <Input
            {...inputProps}
            value={value}
            onChange={onChange}
            aria-label={label}
            ref={inputRef}
            onBlur={trackedOnBlur}
            disabled={isDisabled}
          />
        </TextContainer>
        {iconRight && (
          <FontAwesomeIcon
            icon={iconRight}
            style={{
              height: 20,
              cursor: onIconRightClick ? 'pointer' : 'inherit',
            }}
            onClick={onIconRightClick}
          />
        )}
      </InputContainer>
      {helperText && (
        <HelperText isInvalid={isInvalid} isDisabled={isDisabled}>
          {helperText}
        </HelperText>
      )}
    </Container>
  );
};

export default NewTextField;
